import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserAttribute } from 'amazon-cognito-identity-js'
import * as types from '../mutation-types'

const auth = {
  namespaced: true,
  state: {
    user: undefined,
    jwtToken: undefined,
    updateEmail: undefined,
    error: undefined
  },
  getters: {
    user: state => state.user,
    jwtToken: state => state.jwtToken,
    isLogined: state => {
      if (state.user === undefined) {
        return false
      } else {
        return true
      }
    },
    userName: state => {
      if (state.user === undefined) {
        return 'no user'
      } else {
        return getEmail(state.user)
      }
    },
    isRoleSuper: state => {
      if (state.user === undefined) {
        return false
      } else {
        return isCheckRoleSuper(getRoleId(state.user))
      }
    },
    isRoleAgency: state => {
      if (state.user === undefined) {
        return false
      } else {
        return isCheckRoleAgency(getRoleId(state.user))
      }
    },
    isRoleAdvertiser: state => {
      if (state.user === undefined) {
        return false
      } else {
        return isCheckRoleAdvertiser(getRoleId(state.user))
      }
    },
    sub: state => state.sub,
    error: state => (state.error ? state.error : ''),
    updateEmail: state => (state.updateEmail ? state.updateEmail : ''),
    userMap: state => {
      if (state.user === undefined) {
        return {}
      } else {
        let um = {}
        state.user.forEach(attribute => {
          um[attribute.Name] = attribute.Value
        })
        return um
      }
    }
  },
  mutations: {
    [types.RECEIVE_SETUSER](state, user) {
      state.user = user
    },
    [types.RECEIVE_SETJWTTOKEN](state, jwtToken) {
      state.jwtToken = jwtToken
    },
    [types.RECEIVE_SETUPDATESUB](state, sub) {
      state.sub = sub
    },
    [types.RECEIVE_SETERROR](state, error) {
      state.error = error
    },
    [types.RECEIVE_SETUPDATEMAIL](state, updateEmail) {
      state.updateEmail = updateEmail
    }
  },
  actions: {
    setLoginError({ commit }, error) {
      commit(types.RECEIVE_SETERROR, error)
    },
    setupdateEmail({ commit }, updateEmail) {
      commit(types.RECEIVE_SETUPDATEMAIL, updateEmail)
    },
    getSession({ commit }) {
      return new Promise((resolve, reject) => {
        var userPool = createUserPool()
        var cognitoUser = userPool.getCurrentUser()
        if (cognitoUser == null) {
          commit(types.RECEIVE_SETUSER, undefined)
          reject(new Error('cognitoUser is null.'))
          return
        }
        cognitoUser.getSession(function(err, session) {
          if (err) {
            commit(types.RECEIVE_SETUSER, undefined)
            reject(err)
            return
          }
          commit(types.RECEIVE_SETJWTTOKEN, session.getIdToken().getJwtToken())
          cognitoUser.getUserAttributes(function(err, attributes) {
            if (err) {
              commit(types.RECEIVE_SETUSER, undefined)
              reject(err)
              return
            }
            commit(types.RECEIVE_SETUSER, attributes)
            resolve(attributes)
          })
        })
      })
    },
    login({ commit }, { userName, password }) {
      return new Promise((resolve, reject) => {
        var userPool = createUserPool()
        var userData = {
          Username: userName,
          Pool: userPool
        }
        var authenticationDetails = new AuthenticationDetails({
          Username: userName,
          Password: password
        })
        var cognitoUser = new CognitoUser(userData)
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: function(result) {
            commit(types.RECEIVE_SETJWTTOKEN, result.getIdToken().getJwtToken())
            resolve(result)
          },
          onFailure: function(err) {
            reject(err)
          },
          newPasswordRequired: function(userAttributes, requiredAttributes) {
            // 仮パスワード => 本パスワードのチェックを実施しない場合の書き方
            // 管理者画面からアカウントを追加した場合にこの部分に処理が遷移するので対応を行っている
            // https://docs.aws.amazon.com/ja_jp/cognito/latest/developerguide/using-amazon-cognito-identity-user-pools-javascript-example-authenticating-admin-created-user.html
            cognitoUser.completeNewPasswordChallenge(password, {}, this)
          }
        })
      })
    },
    logout({ commit }) {
      return new Promise((resolve, reject) => { // Promise を返すようにする
        var userPool = createUserPool();
        var cognitoUser = userPool.getCurrentUser();

        if (cognitoUser != null) {
          cognitoUser.signOut(); // 非同期処理だが、CognitoUser.signOut() は Promise を返さないため、コールバックは不要
          // signOut() は通常エラーを投げないため、完了を待つ必要がないが、
          // 念のため、Promiseでラップして確実に非同期として扱う
          // 実際には、signOut() は成功すればそれ以上何もしないため、即座に resolve しても問題ない
          commit(types.RECEIVE_SETUSER, undefined); // Cognitoセッションクリア後にVuexもクリア
          resolve(); // 成功として解決
        } else {
          // cognitoUser が null の場合も、ログアウト状態と見なし成功として解決
          commit(types.RECEIVE_SETUSER, undefined); // 念のためVuexもクリア
          resolve();
        }
      });
    },
    /**
     * username, passwordでサインアップ
     * username = emailとしてUserAttributeにも登録
     */
    signup({ commit }, { email, password }) {
      return new Promise((resolve, reject) => {
        var userPool = createUserPool()
        var attributeList = []
        var dataEmail = {
          Name: 'email',
          Value: email
        }
        var attributeEmail = new CognitoUserAttribute(dataEmail)
        attributeList.push(attributeEmail)

        userPool.signUp(email, password, attributeList, null, function(err, result) {
          if (err) {
            reject(err)
            return
          }
          resolve(result)
        })
      })
    },
    /**
     * パスワードを発行するための
     * @param {*} email メールアドレス
     */
    forget({ commit }, email) {
      const userPool = createUserPool()
      const userData = { Username: email, Pool: userPool }
      const cognitoUser = new CognitoUser(userData)
      return new Promise((resolve, reject) => {
        cognitoUser.forgotPassword({
          onSuccess: function(result) {
            resolve(result)
          },
          onFailure: function(err) {
            reject(err)
          }
        })
      })
    },
    /**
     * パスワードを再設定する
     * @param {*} params
     */
    rePassword({ commit }, params) {
      const userPool = createUserPool()
      const userData = { Username: params.email, Pool: userPool }
      const cognitoUser = new CognitoUser(userData)
      return new Promise((resolve, reject) => {
        cognitoUser.confirmPassword(params.code, params.password, {
          onSuccess: result => {
            resolve(result)
          },
          onFailure: err => {
            reject(err)
          }
        })
      })
    },
    activate({ commit }, { email, key }) {
      return new Promise((resolve, reject) => {
        const userPool = createUserPool()
        const userData = {
          Username: email,
          Pool: userPool
        }

        var cognitoUser = new CognitoUser(userData)
        cognitoUser.confirmRegistration(key, true, function(err, result) {
          if (err) {
            reject(err)
            return
          }
          resolve(result)
        })
      })
    },
    reset({ commit }) {
      commit(types.RECEIVE_SETUSER, undefined)
      commit(types.RECEIVE_SETJWTTOKEN, undefined)
      commit(types.RECEIVE_SETUPDATESUB, undefined)
      commit(types.RECEIVE_SETERROR, undefined)
      commit(types.RECEIVE_SETUPDATEMAIL, undefined)
    }
  }
}

function createUserPool() {
  var poolData = {
    UserPoolId: process.env.UserPoolId,
    ClientId: process.env.ClientId
  }
  var userPool = new CognitoUserPool(poolData)
  return userPool
}

function getEmail(user) {
  var email
  user.forEach(function(attr) {
    if (attr.Name === 'email') {
      email = attr.Value
    }
  })
  return email
}

function getRoleId(user) {
  var result
  user.forEach(function(attr) {
    if (attr.Name === 'custom:roleId') {
      result = attr.Value
    }
  })
  return result
}

function isCheckRoleSuper(roleId) {
  if (roleId === '1') {
    return true
  }
  return false
}
function isCheckRoleAgency(roleId) {
  if (roleId === '2' || roleId == null) {
    return true
  }
  return false
}
function isCheckRoleAdvertiser(roleId) {
  if (roleId === '3') {
    return true
  }
  return false
}

export default auth
